import * as React from "react";
import ReactDOM from "react-dom";
import {
    Edit,
    FormWithRedirect,
    TextInput,
    Toolbar,
    SaveButton,
    ReferenceInput,
    SelectInput,
    DateInput,
    ResourceContextProvider,
    BooleanInput,
    NumberInput,
    List,
    DeleteButton,
    EditButton,
    TextField,
    Datagrid,
    useDataProvider,
    useRefresh,
    Button,
    Create,
    SimpleForm,
    useMutation,
    useRedirect,
    useNotify,
} from 'react-admin';

import { Typography, Box, Tabs, Tab, Divider, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField as MuiTextField, Checkbox, FormControlLabel, Chip, Button as MuiButton, ThemeProvider, createTheme } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import RefreshIcon from '@material-ui/icons/Refresh';

import { EmploymentUpdateRequestList } from './employment-update-requests';
import { HiatusesPanel } from './settings';
import { AddEmploymentUsers, EmploymentUsersList } from "./employment-users";
import PenaltyGroupAssignmentPanel from './components/PenaltyGroupAssignmentPanel';

import GetAppIcon from "@material-ui/icons/GetApp";
import { downloadGetFileResult } from "../functions/downloadGetFileResult";
import ProductionEmploymentsImportForm from "./components/ProductionEmploymentsImportForm";

const ALLOWANCE_TYPES = [
    { value: 'box', label: 'Box' },
    { value: 'car', label: 'Car' },
    { value: 'computer', label: 'Computer' },
    { value: 'phone', label: 'Phone' },
    { value: 'living', label: 'Living' },
    { value: 'hire_car', label: 'Hire Car' },
    { value: 'first_aid', label: 'First Aid' },
    { value: 'equipment', label: 'Equipment' },
];

// ---------------------------------------------------------------------------
// Job Title Warning Modal
// ---------------------------------------------------------------------------



// ---------------------------------------------------------------------------
// Allowances Panel
// ---------------------------------------------------------------------------

const EmploymentAllowancesPanel = ({ employmentId, productionId }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [allowances, setAllowances] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [editing, setEditing] = React.useState(null);
    const [form, setForm] = React.useState({
        type: 'box', daily_amount: '', prep: false, shoot: false, wrap: false, cap: '', description: ''
    });

    const load = () => {
        setLoading(true);
        dataProvider.getList(`employees/${employmentId}/allowances`, { pagination: {}, sort: {}, filter: {} })
            .then(({ data }) => { setAllowances(data); setLoading(false); })
            .catch(() => setLoading(false));
    };

    React.useEffect(() => { load(); }, [employmentId]);

    const openAdd = () => {
        setEditing(null);
        setForm({ type: 'box', daily_amount: '', prep: false, shoot: false, wrap: false, cap: '', description: '' });
        setDialogOpen(true);
    };

    const openEdit = (allowance) => {
        setEditing(allowance);
        setForm({
            type: allowance.type,
            daily_amount: allowance.daily_amount,
            prep: allowance.prep,
            shoot: allowance.shoot,
            wrap: allowance.wrap,
            cap: allowance.cap ?? '',
            description: allowance.description ?? '',
        });
        setDialogOpen(true);
    };

    const handleSave = () => {
        const payload = { ...form };
        if (!payload.cap) delete payload.cap;
        if (!payload.description) delete payload.description;

        const request = editing
            ? dataProvider.update(`employees/${employmentId}/allowances`, { id: editing.id, data: payload })
            : dataProvider.create(`employees/${employmentId}/allowances`, { data: payload });

        request.then(() => {
            notify(editing ? 'Allowance updated' : 'Allowance added', 'success');
            setDialogOpen(false);
            load();
        }).catch(err => {
            notify(err?.body?.type?.[0] ?? 'Error saving allowance', 'error');
        });
    };

    const handleDelete = (id) => {
        dataProvider.delete(`employees/${employmentId}/allowances`, { id })
            .then(() => { notify('Allowance deleted', 'success'); load(); })
            .catch(() => notify('Error deleting allowance', 'error'));
    };

    return (
        <Box mb={3}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6">Allowances</Typography>
                <Button label="Add Allowance" onClick={openAdd}><AddIcon /></Button>
            </Box>
            {loading ? <Typography variant="body2">Loading...</Typography> : (
                allowances.length === 0
                    ? <Typography variant="body2" color="textSecondary">No allowances added.</Typography>
                    : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Daily</TableCell>
                                    <TableCell>Weekly</TableCell>
                                    <TableCell>Prep</TableCell>
                                    <TableCell>Shoot</TableCell>
                                    <TableCell>Wrap</TableCell>
                                    <TableCell>Cap</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {allowances.map(a => (
                                    <TableRow key={a.id}>
                                        <TableCell>{ALLOWANCE_TYPES.find(t => t.value === a.type)?.label ?? a.type}</TableCell>
                                        <TableCell>£{a.daily_amount}</TableCell>
                                        <TableCell>£{a.weekly_amount ?? '—'}</TableCell>
                                        <TableCell>{a.prep ? '✓' : '—'}</TableCell>
                                        <TableCell>{a.shoot ? '✓' : '—'}</TableCell>
                                        <TableCell>{a.wrap ? '✓' : '—'}</TableCell>
                                        <TableCell>{a.cap ? `£${a.cap}` : '—'}</TableCell>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => openEdit(a)}><EditIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" onClick={() => handleDelete(a.id)}><DeleteIcon fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editing ? 'Edit Allowance' : 'Add Allowance'}</DialogTitle>
                <DialogContent>
                    <Box mb={2}>
                        <MuiTextField
                            select
                            label="Type"
                            value={form.type}
                            onChange={e => setForm({ ...form, type: e.target.value })}
                            fullWidth
                            disabled={!!editing}
                            SelectProps={{ native: true }}
                        >
                            {ALLOWANCE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </MuiTextField>
                    </Box>
                    <Box mb={2}>
                        <MuiTextField
                            label="Daily Amount (£)"
                            type="number"
                            value={form.daily_amount}
                            onChange={e => setForm({ ...form, daily_amount: e.target.value })}
                            fullWidth
                        />
                    </Box>
                    <Box mb={2}>
                        <MuiTextField
                            label="Cap (£) — optional"
                            type="number"
                            value={form.cap}
                            onChange={e => setForm({ ...form, cap: e.target.value })}
                            fullWidth
                        />
                    </Box>
                    <Box mb={2}>
                        <MuiTextField
                            label="Description — optional"
                            value={form.description}
                            onChange={e => setForm({ ...form, description: e.target.value })}
                            fullWidth
                        />
                    </Box>
                    <Box display="flex">
                        <FormControlLabel control={<Checkbox checked={form.prep} onChange={e => setForm({ ...form, prep: e.target.checked })} />} label="Prep" />
                        <FormControlLabel control={<Checkbox checked={form.shoot} onChange={e => setForm({ ...form, shoot: e.target.checked })} />} label="Shoot" />
                        <FormControlLabel control={<Checkbox checked={form.wrap} onChange={e => setForm({ ...form, wrap: e.target.checked })} />} label="Wrap" />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button label="Cancel" onClick={() => setDialogOpen(false)} />
                    <Button label="Save" onClick={handleSave} />
                </DialogActions>
            </Dialog>
        </Box>
    );
};

// ---------------------------------------------------------------------------
// Penalty Rates Panel
// ---------------------------------------------------------------------------

const PenaltyRatesPanel = ({ employmentId }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [rates, setRates] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [editing, setEditing] = React.useState(null);
    const [rateValue, setRateValue] = React.useState('');

    const load = () => {
        setLoading(true);
        dataProvider.getList(`employees/${employmentId}/penalty-rates`, { pagination: {}, sort: {}, filter: {} })
            .then(({ data }) => { setRates(data); setLoading(false); })
            .catch(() => setLoading(false));
    };

    React.useEffect(() => { load(); }, [employmentId]);

    const openEdit = (rate) => {
        setEditing(rate);
        setRateValue(rate.rate);
        setDialogOpen(true);
    };

    const handleSave = () => {
        dataProvider.update(`employees/${employmentId}/penalty-rates`, { id: editing.id, data: { rate: rateValue } })
            .then(() => {
                notify('Rate updated', 'success');
                setDialogOpen(false);
                load();
            })
            .catch(() => notify('Error updating rate', 'error'));
    };

    const handleReset = (rate) => {
        dataProvider.create(`employees/${employmentId}/penalty-rates/${rate.id}/reset`, { data: {} })
            .then(() => { notify('Rate reset to calculated value', 'success'); load(); })
            .catch(() => notify('Error resetting rate', 'error'));
    };

    return (
        <Box mb={3}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6">Penalty Rates</Typography>
            </Box>
            {loading ? <Typography variant="body2">Loading...</Typography> : (
                rates.length === 0
                    ? <Typography variant="body2" color="textSecondary">No penalty rates calculated. Set an hourly rate and assign a penalty group to generate rates.</Typography>
                    : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Rate (£/hr)</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rates.map(r => (
                                    <TableRow key={r.id}>
                                        <TableCell>{r.penalty_type?.full_name ?? r.penalty_type_id}</TableCell>
                                        <TableCell>£{parseFloat(r.rate).toFixed(2)}</TableCell>
                                        <TableCell>
                                            {r.is_override
                                                ? <Chip label="Override" size="small" color="secondary" />
                                                : <Chip label="Calculated" size="small" />
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => openEdit(r)} title="Override rate"><EditIcon fontSize="small" /></IconButton>
                                            {r.is_override && (
                                                <IconButton size="small" onClick={() => handleReset(r)} title="Reset to calculated"><RefreshIcon fontSize="small" /></IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Override Penalty Rate — {editing?.penalty_type?.short_name}</DialogTitle>
                <DialogContent>
                    <Box mt={1}>
                        <MuiTextField
                            label="Rate (£/hr)"
                            type="number"
                            value={rateValue}
                            onChange={e => setRateValue(e.target.value)}
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button label="Cancel" onClick={() => setDialogOpen(false)} />
                    <Button label="Save" onClick={handleSave} />
                </DialogActions>
            </Dialog>
        </Box>
    );
};

// ---------------------------------------------------------------------------
// Misc components
// ---------------------------------------------------------------------------

const ResendInvitationButton = ({ record }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider();

    const resendInvitation = () => dataProvider.create(
        `employees/${record.id}/resend-invitation`,
        { data: {} }
    ).then(() => {
        notify('Invitation has been re-sent', 'success');
        refresh();
    });

    if (record.status === 'Invited') {
        return <Button label="Re-send Invitation" onClick={resendInvitation} />;
    }

    return null;
};

const EmploymentStartFormDownloadButton = ({ record }) => {
    const dataProvider = useDataProvider();

    const downloadStartForm = () => dataProvider.getFile(
        `employees/${record.id}/start-form-pdf`
    ).then(downloadGetFileResult);

    if (!record.is_onboarded) {
        return '';
    }

    return <Button
        variant="contained"
        to={`employments/${record.id}/start-form-pdf`}
        label="Start Form"
        title="Download start form"
        onClick={downloadStartForm}
    >
        <GetAppIcon />
    </Button>;
};

const EmploymentTitle = props => {
    return <span>{props.record ? `${props.record.first_name} ${props.record.surname} - ${props.record.department.name}` : ''}</span>;
};

const SectionHeading = ({ title }) => (
    <Box mt={2} mb={1}>
        <Typography variant="h6" gutterBottom>{title}</Typography>
        <Divider />
    </Box>
);

const ReadOnlyField = ({ label, source, record }) => (
    <Box mb={1} mr={2}>
        <Typography variant="caption" color="textSecondary">{label}</Typography>
        <Typography variant="body2">{record?.[source] ?? '—'}</Typography>
    </Box>
);

const hireTypeChoices = [
    { id: 'local_hire', name: 'Local Hire' },
    { id: 'distant_hire', name: 'Distant Hire' },
];

const prodMeetingChoices = [
    { id: 'work', name: 'Work' },
    { id: 'designated_holiday', name: 'Designated Holiday' },
];

const readThroughChoices = [
    { id: 'work', name: 'Work' },
    { id: 'designated_holiday', name: 'Designated Holiday' },
];

const filters = [
    <TextInput label="Search" source="search" alwaysOn />,
    <ReferenceInput perPage={100} label="Department" source="department_id" reference="departments" emptyText="All" resettable={true} alwaysOn>
        <SelectInput optionText="name" />
    </ReferenceInput>,
    <ReferenceInput perPage={100} label="Payroll" source="payroll_id" reference="payrolls" emptyText="All" resettable={true} alwaysOn>
        <SelectInput optionText="name" />
    </ReferenceInput>,
];

export const EmploymentsList = props => (
    <List {...props} filters={filters} sort={{ field: 'surname', order: 'asc' }}>
        <Datagrid>
            <TextField source="first_name" label="First Name" />
            <TextField source="surname" label="Surname" />
            <TextField source="department.name" label="Department" />
            <TextField source="job_title" label="Job Title" />
            <TextField source="status" label="Status" />
            <ResendInvitationButton />
            <EmploymentStartFormDownloadButton label="Start Form" />
            <EditButton />
            <DeleteButton />
        </Datagrid>
    </List>
);

const PeriodDateFields = ({ prefix, label, record = null }) => (
    <Box mb={2}>
        <Typography variant="subtitle2" gutterBottom>{label}</Typography>
        <DateInput source={`${prefix}_start_date`} label="Start Date" fullWidth />
        <DateInput source={`${prefix}_end_date`} label="End Date" fullWidth />
        {record && (
            <Box display="flex" mt={1}>
                <ReadOnlyField label="Total Days" source={`${prefix}_total_days`} record={record} />
                <ReadOnlyField label="Weeks" source={`${prefix}_weeks`} record={record} />
                <ReadOnlyField label="Days" source={`${prefix}_days`} record={record} />
            </Box>
        )}
    </Box>
);

// ---------------------------------------------------------------------------
// EmploymentsEdit
// ---------------------------------------------------------------------------

// Module-level state — survives component unmount/remount caused by
// FormWithRedirect resetting the Edit component on save failure.
let _warningState = { open: false, jobTitle: '', pendingId: null, pendingValues: null };
let _warningListeners = [];
const setWarningState = (state) => {
    _warningState = { ..._warningState, ...state };
    _warningListeners.forEach(fn => fn(_warningState));
};
const subscribeWarning = (fn) => {
    _warningListeners.push(fn);
    return () => { _warningListeners = _warningListeners.filter(f => f !== fn); };
};

export const JobTitleWarningOverlay = () => {
    const [state, setState] = React.useState(_warningState);

    React.useEffect(() => {
        return subscribeWarning(setState);
    }, []);

    const handleConfirm = React.useCallback(() => {
        const { pendingId, pendingValues } = state;
        setWarningState({ open: false });
        // Re-trigger the update with confirmation — stored on window to avoid
        // circular dependency
        if (window._employmentDoUpdate) {
            window._employmentDoUpdate(pendingId, pendingValues, true);
        }
    }, [state]);

    const handleCancel = React.useCallback(() => {
        setWarningState({ open: false, pendingId: null, pendingValues: null });
    }, []);

    if (!state.open) return null;
    return ReactDOM.createPortal(
        <ThemeProvider theme={createTheme()}>
            <Dialog open={state.open} onClose={handleCancel} maxWidth="sm" fullWidth>
                <DialogTitle>Job title not on HMRC approved list</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        The job title <strong>"{state.jobTitle}"</strong> does not appear on the HMRC
                        approved list for this contract type (Loan-out / Self-Employed).
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        If you proceed, this title will be saved for this production only. It will not
                        be added to the master HMRC list.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <MuiButton onClick={handleCancel}>Cancel — go back</MuiButton>
                    <MuiButton onClick={handleConfirm} color="primary" variant="contained">Confirm and proceed</MuiButton>
                </DialogActions>
            </Dialog>
        </ThemeProvider>,
        document.body
    );
};

export const EmploymentsEdit = props => {
    const notify = useNotify();
    const redirect = useRedirect();
    const dataProvider = useDataProvider();

    const transform = (data) => {
        const d = { ...data };
        delete d.production_id;
        delete d.department;
        return d;
    };

    const doUpdate = React.useCallback(
        (id, values, confirm = false) => {
            const data = confirm ? { ...values, confirm_job_title: true } : values;
            return dataProvider.update('employees', { id, data })
                .then(() => {
                    notify('Employment updated', 'success');
                    redirect('/employees');
                })
                .catch(err => {
                    const warning = err?.body?.warnings?.job_title_not_approved;
                    if (warning) {
                        setWarningState({
                            open: true,
                            jobTitle: warning.job_title,
                            pendingId: id,
                            pendingValues: values,
                        });
                        return;
                    }
                    if (typeof err.body === 'object') {
                        return err.body;
                    }
                    notify(err.message ?? 'Error saving employment', 'error');
                });
        },
        [dataProvider, notify, redirect],
    );

    // Store doUpdate on window so the overlay can call it after confirmation
    React.useEffect(() => {
        window._employmentDoUpdate = doUpdate;
        return () => { window._employmentDoUpdate = null; };
    }, [doUpdate]);

    const save = React.useCallback((values) => {
        const transformed = transform(values);
        doUpdate(transformed.id ?? values.id, transformed);
    }, [doUpdate]);

    return (
        <Edit {...props} title={<EmploymentTitle />} undoable={false}>
            <FormWithRedirect
                save={save}
                render={formProps => {
                    const record = formProps.record;
                    const rateType = record?.rate_type;
                    const hasAgent = record?.has_agent;

                    return (
                        <Box p="1em">
                            <Box display="flex">
                                <Box flex={1} mr="1em">
                                    <form>
                                        <SectionHeading title="Personal Details" />
                                        <TextInput source="first_name" fullWidth />
                                        <TextInput source="surname" fullWidth />
                                        <TextInput source="phone" fullWidth />
                                        <ReferenceInput label="Department" source="department_id" reference="departments" fullWidth filter={{ production_id: record.production_id }} perPage={100}>
                                            <SelectInput optionText="name" optionValue="id" />
                                        </ReferenceInput>
                                        <ReferenceInput label="Payroll" source="payroll_id" reference="payrolls" fullWidth filter={{ production_id: record.production_id }} perPage={100} allowEmpty>
                                            <SelectInput optionText="name" optionValue="id" />
                                        </ReferenceInput>
                                        <TextInput source="payroll_number" label="Payroll Number" fullWidth />
                                        <TextInput source="job_title" fullWidth />

                                        <SectionHeading title="Contract Details" />
                                        <SelectInput source="contract_type" choices={[
                                            { id: 'daily', name: 'Daily' },
                                            { id: 'contract', name: 'Contract' },
                                        ]} fullWidth />
                                        <SelectInput source="hire_type" choices={hireTypeChoices} fullWidth allowEmpty />
                                        <SelectInput source="employment_type" label="Employment Type" choices={[
                                            { id: 'paye', name: 'PAYE' },
                                            { id: 'ltdCo', name: 'Loan-out' },
                                            { id: 'soleTrader', name: 'Self-Employed' },
                                        ]} fullWidth allowEmpty />
                                        <DateInput source="employment_start_date" fullWidth />
                                        <DateInput source="employment_end_date" fullWidth />
                                        <DateInput source="access_end_date" fullWidth helperText="Defaults to one week after the employment end date if not provided" />
                                        <TextInput source="start_time" fullWidth />
                                        <TextInput source="end_time" fullWidth />
                                        <SelectInput source="prod_meeting" label="Prod Meeting" choices={prodMeetingChoices} fullWidth allowEmpty />
                                        <SelectInput source="read_through" label="Read Through" choices={readThroughChoices} fullWidth allowEmpty />
                                        <Box display="flex" mt={1}>
                                            <ReadOnlyField label="Total Days" source="total_days" record={record} />
                                            <ReadOnlyField label="Total Weeks" source="total_weeks" record={record} />
                                            <ReadOnlyField label="Remaining Days" source="total_days_left" record={record} />
                                        </Box>

                                        <SectionHeading title="Rates" />
                                        <SelectInput source="rate_type" choices={[
                                            { id: 'daily', name: 'Daily' },
                                            { id: 'hourly', name: 'Hourly' },
                                        ]} fullWidth />
                                        <NumberInput source="days_per_week" label="Days Per Week" fullWidth helperText="e.g. 5" />
                                        <NumberInput source="daily_basic_hours" label="Daily Basic Hours" fullWidth helperText="e.g. 10" />
                                        {rateType === 'daily' && (
                                            <NumberInput source="daily_rate_gross" label="Daily Rate (Gross)" fullWidth />
                                        )}
                                        {rateType === 'hourly' && (
                                            <NumberInput source="hourly_rate_gross" label="Hourly Rate (Gross)" fullWidth />
                                        )}
                                        <Box mt={2}>
                                            <Typography variant="subtitle2" gutterBottom>Calculated Rates</Typography>
                                            <Box display="flex" flexWrap="wrap">
                                                <ReadOnlyField label="Daily Rate (Net)" source="daily_rate_net" record={record} />
                                                <ReadOnlyField label="Daily Rate (Holiday)" source="daily_rate_holiday" record={record} />
                                                <ReadOnlyField label="Hourly Rate (Gross)" source="hourly_rate_gross" record={record} />
                                                <ReadOnlyField label="Hourly Rate (Net)" source="hourly_rate_net" record={record} />
                                                <ReadOnlyField label="Hourly Rate (Holiday)" source="hourly_rate_hol" record={record} />
                                                <ReadOnlyField label="Weekly Rate (Gross)" source="weekly_rate_gross" record={record} />
                                                <ReadOnlyField label="Weekly Rate (Net)" source="weekly_rate_net" record={record} />
                                                <ReadOnlyField label="Weekly Rate (Holiday)" source="weekly_rate_hol" record={record} />
                                                <ReadOnlyField label="Weekly ACH (Gross)" source="weekly_gross_ach" record={record} />
                                                <ReadOnlyField label="Weekly ACH (Net)" source="weekly_net_ach" record={record} />
                                                <ReadOnlyField label="Weekly ACH (Holiday)" source="weekly_hol_ach" record={record} />
                                            </Box>
                                        </Box>

                                        <SectionHeading title="Allowances" />
                                        <BooleanInput source="per_diems" label="Per Diems" />

                                        <SectionHeading title="Period Dates" />
                                        <PeriodDateFields prefix="pre_prep" label="Pre-Prep" record={record} />
                                        <PeriodDateFields prefix="prep" label="Prep" record={record} />
                                        <PeriodDateFields prefix="shoot" label="Shoot" record={record} />
                                        <PeriodDateFields prefix="wrap" label="Wrap" record={record} />

                                        <SectionHeading title="Agent" />
                                        <BooleanInput source="has_agent" label="Has Agent" />
                                        {hasAgent && (
                                            <>
                                                <TextInput source="agent_name" fullWidth />
                                                <TextInput source="agent_email" fullWidth />
                                                <TextInput source="agent_address" fullWidth multiline />
                                            </>
                                        )}

                                        <SectionHeading title="Production Flags" />
                                        <BooleanInput source="is_shoot_crew" label="Shoot Crew" />
                                        <BooleanInput source="is_hod" label="Head of Department" />
                                        <BooleanInput source="is_trainee" label="Trainee" />
                                        <BooleanInput source="is_vat_registered" label="VAT Registered" />
                                        <BooleanInput source="ach" label="Additional Contracted Hour (ACH)" />
                                        <BooleanInput source="is_onboarded" label="Is Onboarded?" />

                                        <Box display="flex" justifyContent="space-between" width="100%">
                                            <Toolbar disableGutters={true}>
                                                <SaveButton
                                                    saving={formProps.saving}
                                                    handleSubmitWithRedirect={formProps.handleSubmitWithRedirect}
                                                />
                                                <ResendInvitationButton record={record} />
                                            </Toolbar>
                                        </Box>
                                    </form>
                                </Box>

                                <Box flex={2} ml="1em">
                                    <EmploymentAllowancesPanel
                                        employmentId={record.id}
                                        productionId={record.production_id}
                                    />

                                    <PenaltyRatesPanel employmentId={record.id} />

                                    <HiatusesPanel
                                        resource={`employees/${record.id}/hiatuses`}
                                        label="Hiatuses"
                                    />

                                    <Typography variant="h6" gutterBottom>Users</Typography>
                                    <ResourceContextProvider value="employment-users">
                                        <EmploymentUsersList id={record.id} />
                                    </ResourceContextProvider>

                                    <AddEmploymentUsers employmentId={record.id} />

                                    <Typography variant="h6" gutterBottom>Update Requests</Typography>
                                    <ResourceContextProvider value="employment-update-requests">
                                        <EmploymentUpdateRequestList id={record.id} />
                                    </ResourceContextProvider>

                                    <PenaltyGroupAssignmentPanel
                                        type="employment"
                                        id={record.id}
                                        productionId={record.production_id}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    );
                }}
            />
        </Edit>
    );
};

// ---------------------------------------------------------------------------
// CreateEmployment
// ---------------------------------------------------------------------------

const TabPanel = (props) => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
};

export const CreateEmployment = (props) => {
    const [value, setValue] = React.useState(0);
    const [rateType, setRateType] = React.useState(null);
    const [hasAgent, setHasAgent] = React.useState(false);
    const [warningOpen, setWarningOpen] = React.useState(false);
    const [warningJobTitle, setWarningJobTitle] = React.useState('');
    const pendingValuesRef = React.useRef(null);
    const redirect = useRedirect();
    const notify = useNotify();

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const [mutate] = useMutation();

    // Performs the actual create call, with an optional confirm flag
    const doCreate = React.useCallback(
        (values, confirm = false) => {
            const data = confirm ? { ...values, confirm_job_title: true } : values;
            return mutate({
                type: 'create',
                resource: 'employees',
                payload: { data },
            }, { returnPromise: true })
                .then(() => {
                    notify('ra.notification.created', 'success');
                    redirect('/employees');
                })
                .catch(error => {
                    const warning = error?.body?.warnings?.job_title_not_approved;
                    if (warning) {
                        pendingValuesRef.current = values;
                        setWarningJobTitle(warning.job_title);
                        setWarningOpen(true);
                        return;
                    }
                    if (typeof error.body === 'object') {
                        return error.body;
                    }
                    throw error;
                });
        },
        [mutate, notify, redirect],
    );

    const save = React.useCallback(
        (values) => doCreate(values),
        [doCreate],
    );

    const handleConfirm = () => {
        setWarningOpen(false);
        doCreate(pendingValuesRef.current, true);
    };

    const handleCancel = () => {
        setWarningOpen(false);
        pendingValuesRef.current = null;
    };

    return (
        <>
            <Dialog open={warningOpen} onClose={handleCancel} maxWidth="sm" fullWidth>
                <DialogTitle>Job title not on HMRC approved list</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        The job title <strong>"{warningJobTitle}"</strong> does not appear on the HMRC approved
                        list for this contract type (Loan-out / Self-Employed).
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        If you proceed, this title will be saved for this production only. It will not
                        be added to the master HMRC list.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button label="Cancel — go back" onClick={handleCancel} />
                    <Button label="Confirm and proceed" onClick={handleConfirm} />
                </DialogActions>
            </Dialog>
            <Box sx={{ width: '100%' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                        <Tab label="Bulk Import" />
                        <Tab label="Manual Entry" />
                    </Tabs>
                </Box>
                <TabPanel value={value} index={0}>
                    <ProductionEmploymentsImportForm {...props} />
                </TabPanel>
                <TabPanel value={value} index={1}>
                    <Create {...props} redirect="list" title="Create Employment">
                        <SimpleForm save={save}>
                            <SectionHeading title="Personal Details" />
                            <TextInput source="first_name" fullWidth />
                            <TextInput source="surname" fullWidth />
                            <TextInput source="email" fullWidth />
                            <TextInput source="phone" fullWidth />
                            <ReferenceInput label="Department" source="department_id" reference="departments" perPage={100} fullWidth>
                                <SelectInput optionText="name" />
                            </ReferenceInput>
                            <ReferenceInput label="Payroll" source="payroll_id" reference="payrolls" perPage={100} allowEmpty fullWidth>
                                <SelectInput optionText="name" optionValue="id" />
                            </ReferenceInput>
                            <TextInput source="payroll_number" label="Payroll Number" fullWidth />
                            <TextInput source="job_title" fullWidth />

                            <SectionHeading title="Contract Details" />
                            <SelectInput source="contract_type" choices={[
                                { id: 'daily', name: 'Daily' },
                                { id: 'contract', name: 'Contract' },
                            ]} fullWidth />
                            <SelectInput source="hire_type" choices={hireTypeChoices} fullWidth allowEmpty />
                            <SelectInput source="employment_type" label="Employment Type" choices={[
                                { id: 'paye', name: 'PAYE' },
                                { id: 'ltdCo', name: 'Loan-out' },
                                { id: 'soleTrader', name: 'Self-Employed' },
                            ]} fullWidth allowEmpty />
                            <DateInput source="employment_start_date" fullWidth />
                            <DateInput source="employment_end_date" fullWidth />
                            <TextInput source="start_time" fullWidth />
                            <TextInput source="end_time" fullWidth />
                            <SelectInput source="prod_meeting" label="Prod Meeting" choices={prodMeetingChoices} fullWidth allowEmpty />
                            <SelectInput source="read_through" label="Read Through" choices={readThroughChoices} fullWidth allowEmpty />

                            <SectionHeading title="Rates" />
                            <SelectInput source="rate_type" choices={[
                                { id: 'daily', name: 'Daily' },
                                { id: 'hourly', name: 'Hourly' },
                            ]} fullWidth onChange={e => setRateType(e.target.value)} />
                            <NumberInput source="days_per_week" label="Days Per Week" fullWidth helperText="e.g. 5" />
                            <NumberInput source="daily_basic_hours" label="Daily Basic Hours" fullWidth helperText="e.g. 10" />
                            {rateType === 'daily' && (
                                <NumberInput source="daily_rate_gross" label="Daily Rate (Gross)" fullWidth />
                            )}
                            {rateType === 'hourly' && (
                                <NumberInput source="hourly_rate_gross" label="Hourly Rate (Gross)" fullWidth />
                            )}

                            <SectionHeading title="Allowances" />
                            <BooleanInput source="per_diems" label="Per Diems" defaultValue={false} />

                            <SectionHeading title="Period Dates" />
                            <PeriodDateFields prefix="pre_prep" label="Pre-Prep" />
                            <PeriodDateFields prefix="prep" label="Prep" />
                            <PeriodDateFields prefix="shoot" label="Shoot" />
                            <PeriodDateFields prefix="wrap" label="Wrap" />

                            <SectionHeading title="Agent" />
                            <BooleanInput source="has_agent" label="Has Agent" onChange={e => setHasAgent(e.target.checked)} />
                            {hasAgent && (
                                <>
                                    <TextInput source="agent_name" fullWidth />
                                    <TextInput source="agent_email" fullWidth />
                                    <TextInput source="agent_address" fullWidth multiline />
                                </>
                            )}

                            <SectionHeading title="Production Flags" />
                            <BooleanInput source="is_shoot_crew" label="Shoot Crew" />
                            <BooleanInput source="is_hod" label="Head of Department" />
                            <BooleanInput source="is_trainee" label="Trainee" />
                            <BooleanInput source="is_vat_registered" label="VAT Registered" />
                            <BooleanInput source="ach" label="Additional Contracted Hour (ACH)" />
                            <BooleanInput source="is_onboarded" label="Is Onboarded?" />
                        </SimpleForm>
                    </Create>
                </TabPanel>
            </Box>
        </>
    );
};
